import { Translations } from '../types';

export const translations: Record<'en' | 'zh', Translations> = {
  en: {
    hero: {
      title: 'Your AI Quant Assistant — Built for Traders',
      subtitle: 'AlphoraAI',
      description: 'Supercharge your trading insights with real-time AI research, pattern recognition, and strategy optimization.',
      ctaButton: 'Join the Waitlist',
      demoButton: 'Try Demo',
      credibility: 'Built by Citadel quant researchers and AI engineers',
      limited: 'Spots are limited — get early access before public launch'
    },
    features: {
      title: 'Powerful Features',
      items: [
        {
          title: 'AI-Powered Research',
          description: 'Real-time market analysis powered by advanced machine learning algorithms that identify patterns and opportunities.'
        },
        {
          title: 'Pattern Recognition',
          description: 'Automated detection of complex trading patterns and market anomalies across multiple timeframes.'
        },
        {
          title: 'Strategy Optimization',
          description: 'AI-driven backtesting and optimization of trading strategies with risk-adjusted performance metrics.'
        },
        {
          title: 'Live Market Insights',
          description: 'Instant notifications and alerts based on real-time market conditions and AI-generated signals.'
        },
        {
          title: 'Multi-Asset Coverage',
          description: 'Crypto: BTC, ETH, BNB, SOL, XRP. Stocks: AAPL, GOOGL, MSFT, TSLA, NVDA, AMZN, META, GC=F (Gold), NASDAQ.'
        },
        {
          title: 'Pro Indicators',
          description: 'RSI, MACD, EMA, and a growing indicator library to validate momentum, trend, and entries.'
        }
      ]
    },
    demo: {
      title: 'See AlphoraAI in Action',
      subtitle: 'Real-time pattern recognition and strategy optimization',
      performance: 'Strategy Performance: +23.7%',
      liveAnalysis: 'Live AI Analysis'
    },
    whatsNew: {
      title: "What's New",
      subtitle: 'Fresh drops from the team — live and ready to use',
      items: [
        {
          tag: 'Just launched',
          title: 'Automated AI Agent for Drawing Tools',
          description: 'Rectangle and point tools now auto-run with the AI agent — drop your levels and let Alphora handle the analysis.'
        },
        {
          tag: 'Notifications on',
          title: 'Signal Alerts, Hands-Off',
          description: 'Stay on top of AI findings with instant notifications. Alphora watches the market; you just get the signal.'
        }
      ]
    },
    about: {
      title: 'About Us',
      subtitle: 'Ivy League researchers and big-tech engineers',
      mission: 'We learn fast, build fast, and launch fast with our community.',
      description: 'Alphora is a team of quants, ML engineers, and product builders shipping AI-native trading tools that stay ahead of the market. Join our community to stay on the leading edge of market trends.',
      links: [
        { label: 'LinkedIn', href: 'https://www.linkedin.com/company/alphora-ai/posts/?feedView=all', icon: 'linkedin' },
        { label: 'X', href: 'https://x.com/alphora_ai?s=11&t=vHF_skXh-JXmy-K2WxkJGw', icon: 'x' },
        { label: 'Discord', href: 'https://discord.gg/ZgmPZFk9', icon: 'discord' },
        { label: 'YouTube', href: 'https://www.youtube.com/@Alphora-o4y/videos', icon: 'youtube' }
      ]
    },
    nav: [
      { label: 'Home', target: 'hero' },
      { label: 'Features', target: 'features' },
      { label: 'Demo', target: 'demo' },
      { label: "What's New", target: 'whats-new' },
      { label: 'Roadmap', target: 'roadmap' },
      { label: 'About Us', target: 'about' },
      { label: 'Community', target: 'community' },
      { label: 'FAQ', target: 'faq' }
    ],
    social: {
      title: 'Trusted by Leading Traders',
      subtitle: 'Tested by early traders',
      testimonial: 'AlphoraAI has transformed how we approach quantitative research. The AI insights are incredibly accurate and useful.',
      author: '— Senior Quant Trader, Tier-1 Investment Firm',
      testimonials: [
        {
          text: 'As a complete beginner, AlphoraAI made trading so much easier to understand. The AI helped me learn patterns quickly and build confidence.',
          author: 'Anonymous User',
          role: 'New Trader'
        },
        {
          text: 'Working in Web3 and crypto, I needed advanced tools. AlphoraAI\'s AI analysis has been incredibly valuable for my DeFi strategies.',
          author: 'Anonymous User', 
          role: 'Crypto/Web3 Developer'
        }
      ]
    },
    milestones: {
      title: 'Product Roadmap',
      subtitle: 'What we’re shipping next for AlphoraAI',
      items: [
        {
          date: 'Q1 2026',
          title: 'Public Launch + AI Agent',
          description: 'We’re live for everyone with analysis tools, dashboards, and the AI agent guiding trades — join the community and start building.',
          status: 'Live'
        },
        {
          date: 'Q2 2026',
          title: 'AI Agent for Building Strategies',
          description: 'An AI agent that assembles strategies from your prompts, stress-tests with backtests, and packages runbooks ready for execution.',
          status: 'In progress'
        },
        {
          date: 'Q3 2026',
          title: 'Strategy Marketplace',
          description: 'Community strategies, shared backtests, and remixable templates with transparent performance.'
        },
        {
          date: 'Q4 2026',
          title: 'Enterprise Guardrails',
          description: 'SSO, granular roles, audit policies, and dedicated compliance reporting.'
        }
      ]
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        {
          question: 'Is this suitable for retail traders?',
          answer: 'Yes! AlphoraAI is designed for both professional and retail traders, with intuitive interfaces that make advanced quantitative analysis accessible to everyone.'
        },
        {
          question: 'How does the AI agent work?',
          answer: 'Our AI agent continuously analyzes market data, identifies patterns, and generates actionable insights using advanced machine learning models trained on historical market data.'
        },
        {
          question: 'What markets are supported?',
          answer: 'Currently we support major cryptocurrency markets, with plans to expand to forex, equities, and commodities soon.'
        }
      ]
    },
    waitlist: {
      title: 'Get Early Access',
      subtitle: 'Be among the first to experience the future of trading analytics',
      emailPlaceholder: 'Enter your email address',
      submitButton: 'Join Waitlist',
      successMessage: 'Thank you! You\'ve been added to our waitlist.',
      errorMessage: 'Please enter a valid email address.'
    },
    footer: {
      copyright: '© 2026 AlphoraAI. All rights reserved.'
    }
  },
  zh: {
    hero: {
      title: '您的AI量化助手 — 为交易者打造',
      subtitle: 'AlphoraAI',
      description: '通过实时AI研究、模式识别和策略优化，为您的交易洞察加速。',
      ctaButton: '加入等待列表',
      demoButton: '试用演示',
      credibility: '由Citadel量化研究员和AI工程师打造',
      limited: '名额有限 — 在公开发布前获得抢先体验'
    },
    features: {
      title: '强大功能',
      items: [
        {
          title: 'AI驱动的研究',
          description: '由先进机器学习算法驱动的实时市场分析，识别模式和机会。'
        },
        {
          title: '模式识别',
          description: '自动检测复杂的交易模式和市场异常，跨越多个时间框架。'
        },
        {
          title: '策略优化',
          description: 'AI驱动的交易策略回测和优化，具有风险调整的绩效指标。'
        },
        {
          title: '实时市场洞察',
          description: '基于实时市场条件和AI生成信号的即时通知和提醒。'
        },
        {
          title: '多资产覆盖',
          description: '加密：BTC、ETH、BNB、SOL、XRP。股票：AAPL、GOOGL、MSFT、TSLA、NVDA、AMZN、META、GC=F（金价）、NASDAQ。'
        },
        {
          title: '专业指标',
          description: '支持 RSI、MACD、EMA 等，持续扩展的指标库验证动量、趋势与入场点。'
        }
      ]
    },
    demo: {
      title: '看AlphoraAI的实际表现',
      subtitle: '实时模式识别和策略优化',
      performance: '策略表现：+23.7%',
      liveAnalysis: '实时AI分析'
    },
    whatsNew: {
      title: '最新动态',
      subtitle: '团队新鲜发布 — 立即可用',
      items: [
        {
          tag: '刚上线',
          title: '绘图工具的自动化AI代理',
          description: '矩形和点位工具现已由AI代理自动运行 — 标记关键位，剩下的分析交给Alphora。'
        },
        {
          tag: '通知开启',
          title: '信号提醒，全程托管',
          description: '随时接收AI洞察的即时提醒。Alphora 负责监控市场，你只需获取信号。'
        }
      ]
    },
    about: {
      title: '关于我们',
      subtitle: '常春藤学者 + Big Tech 工程师',
      mission: '和社区一起快速学习、快速构建、快速发布。',
      description: 'Alphora 是一支由量化研究员、机器学习工程师与产品人组成的团队，持续交付领先市场的AI原生交易工具。加入社区，第一时间掌握市场趋势与洞察。',
      links: [
        { label: 'LinkedIn', href: 'https://www.linkedin.com/company/alphora-ai/posts/?feedView=all', icon: 'linkedin' },
        { label: 'X', href: 'https://x.com/alphora_ai?s=11&t=vHF_skXh-JXmy-K2WxkJGw', icon: 'x' },
        { label: 'Discord', href: 'https://discord.gg/ZgmPZFk9', icon: 'discord' },
        { label: 'YouTube', href: 'https://www.youtube.com/@Alphora-o4y/videos', icon: 'youtube' }
      ]
    },
    nav: [
      { label: '首页', target: 'hero' },
      { label: '功能', target: 'features' },
      { label: '演示', target: 'demo' },
      { label: '最新动态', target: 'whats-new' },
      { label: '路线图', target: 'roadmap' },
      { label: '关于我们', target: 'about' },
      { label: '社区', target: 'community' },
      { label: 'FAQ', target: 'faq' }
    ],
    social: {
      title: '获得领先交易者信任',
      subtitle: '被早期交易者试用',
      testimonial: 'AlphoraAI彻底改变了我们的量化研究方式。AI洞察极其准确，显著提升了我们的交易表现和决策质量。',
      author: '— 一级投资公司高级量化交易员',
      testimonials: [
        {
          text: '作为交易新手，AlphoraAI让复杂的市场变得易懂。AI分析帮助我快速掌握交易技巧，现在交易更有信心了。',
          author: '匿名用户',
          role: '新手交易者'
        },
        {
          text: '在Web3和加密领域工作，需要专业的交易工具。AlphoraAI的AI分析为我的DeFi策略提供了巨大价值。',
          author: '匿名用户',
          role: 'Crypto/Web3开发者'
        }
      ]
    },
    milestones: {
      title: '产品路线图',
      subtitle: 'AlphoraAI 接下来要交付的关键里程碑',
      items: [
        {
          date: '2026 Q1',
          title: '公开发布 + AI代理',
          description: '现已向所有用户开放，提供分析工具、实时看板，并启用AI代理引导交易 —— 加入社区一起共建。',
          status: '已上线'
        },
        {
          date: '2026 Q2',
          title: 'AI 策略搭建代理',
          description: '基于提示自动搭建完整策略，组合模块、回测压力测试，并输出可直接执行的运行手册。',
          status: '进行中'
        },
        {
          date: '2026 Q3',
          title: '策略市集',
          description: '社区策略与回测共享，模板可复用，绩效透明可追踪。'
        },
        {
          date: '2026 Q4',
          title: '企业级防护',
          description: '提供SSO、细粒度权限、审计策略以及专属合规报表。'
        }
      ]
    },
    faq: {
      title: '常见问题',
      items: [
        {
          question: '这适合散户交易者吗？',
          answer: '当然！AlphoraAI专为专业和散户交易者设计，具有直观的界面，让每个人都能轻松使用先进的量化分析。'
        },
        {
          question: 'AI代理是如何工作的？',
          answer: '我们的AI代理持续分析市场数据，识别模式，并使用在历史市场数据上训练的先进机器学习模型生成可行的洞察。'
        },
        {
          question: '支持哪些市场？',
          answer: '目前我们支持主要的加密货币市场，计划很快扩展到外汇、股票和大宗商品。'
        }
      ]
    },
    waitlist: {
      title: '获取抢先体验',
      subtitle: '成为首批体验交易分析未来的用户',
      emailPlaceholder: '请输入您的邮箱地址',
      submitButton: '加入等待列表',
      successMessage: '谢谢！您已成功加入我们的等待列表。',
      errorMessage: '请输入有效的邮箱地址。'
    },
    footer: {
      copyright: '© 2026 AlphoraAI. 保留所有权利。'
    }
  }
}; 
